import { Link, Navigate } from "@tanstack/react-router";
import type { Merchant, PortfolioAssuranceMetrics, PortfolioAssuranceOverview } from "@unbox/api";
import { Activity, ArrowDown, ArrowUp, BarChartSquare02, Clock, ShieldZap, VideoRecorder } from "@untitledui/icons";
import type React from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { usePortfolioAssuranceOverview } from "@/api/assurance/assurance-overview";
import { Table, TableCard } from "@/components/application/table/table";
import { usePortfolio } from "@/providers/PortfolioProvider";
import { cx } from "@/utils/cx";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { MetricCard } from "../assurance/assurance-dashboard-v2/components";

interface DashboardHeaderProps {
  merchants?: Merchant[];
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ merchants = [] }) => {
  const updateDate = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="self-stretch inline-flex flex-col justify-center items-start gap-12 pb-20">
      <div className="flex justify-between items-center gap-3 self-stretch">
        <div className="flex flex-row gap-1 items-center">
          <ShieldZap size={20} className="text-fg-secondary" />
          <div className="text-fg-secondary text-md font-medium">Safety Overview</div>
        </div>
        <div className="text-gray-600 text-xs font-normal leading-4">Updated {updateDate}</div>
      </div>
      <div className="self-stretch flex justify-between items-end">
        <div className="text-indigo-950 text-3xl max-w-120 font-normal leading-9">
          Summary of risks identified across your portfolio
        </div>
        <div className="flex -space-x-1">
          {merchants.map((merchant) =>
            merchant.logoUrl ? (
              <img
                key={merchant.id}
                src={merchant.logoUrl}
                alt={merchant.name}
                className="w-8 h-8 rounded border-2 border-white object-cover"
              />
            ) : (
              <div
                key={merchant.id}
                className="w-8 h-8 rounded border-2 border-white bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500"
              >
                {merchant.name.slice(0, 2).toUpperCase()}
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

interface TopMetricsProps {
  data: PortfolioAssuranceOverview;
  merchants: Merchant[];
}

const MERCHANT_COLORS = ["#abefc6", "#6fe3cb", "#00d4d8", "#00c4ea", "#00b0f9", "#0099ff", "#1d7cf5", "#7f56d9"];
const TopMetrics: React.FC<TopMetricsProps> = ({ data, merchants }) => {
  const merchantEntries = Object.entries(data);
  const totalVideos = merchantEntries.reduce((sum, [, m]) => sum + m.totalVideos, 0);
  const totalFlags = merchantEntries.reduce((sum, [, m]) => sum + m.totalFlags, 0);
  const videosWithRiskCount = merchantEntries.reduce(
    (sum, [, m]) => sum + Math.round(m.totalVideos * m.videosWithFlagsPercentageAfterAddedAndDismissed),
    0,
  );
  const videosAtRiskPercentage = totalVideos > 0 ? Math.round((videosWithRiskCount / totalVideos) * 100) : 0;

  const pieData = merchantEntries.map(([merchantId, m], index) => {
    const merchant = merchants.find((mer) => mer.id === merchantId);
    return {
      id: merchantId,
      name: merchant?.name ?? merchantId,
      value: m.totalFlags,
      color: MERCHANT_COLORS[index % MERCHANT_COLORS.length],
    };
  });

  return (
    <div className="self-stretch p-8 bg-primary rounded-lg inline-flex flex-col justify-start items-start gap-12">
      <div className="flex items-center gap-2">
        <BarChartSquare02 className="w-5 h-5 text-tertiary" />
        <h4 className="text-base font-medium text-secondary">All Brands Overview</h4>
      </div>
      <div className="self-stretch bg-primary rounded-lg inline-flex justify-start items-start gap-20">
        {/* Card 1: Flagged videos breakdown by merchant */}
        <div className="w-80 h-60 relative flex items-center justify-center">
          {/* Center label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
            <span className="text-2xl font-semibold text-primary">{videosAtRiskPercentage}%</span>
            <span className="text-xs text-tertiary">at risk</span>
          </div>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={115} dataKey="value" stroke="none">
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${
                      // biome-ignore lint/suspicious/noArrayIndexKey: explanation
                      index
                    }`}
                    fill={entry.color}
                  />
                ))}
              </Pie>
              <RechartsTooltip
                wrapperStyle={{ zIndex: 100 }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    const percentage = totalFlags > 0 ? Math.round((item.value / totalFlags) * 100) : 0;
                    // Find merchant's total videos for the "X out of Y videos" display
                    const merchantMetrics = data[item.id];
                    const merchantVideos = merchantMetrics?.totalVideos ?? 0;
                    const videosWithRisks = Math.round(
                      merchantVideos * (merchantMetrics?.videosWithFlagsPercentageAfterAddedAndDismissed ?? 0),
                    );
                    return (
                      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4 min-w-48">
                        <p className="text-base font-semibold text-gray-900">
                          {item.name}: {percentage}%
                        </p>
                        <p className="text-sm text-gray-500">
                          {videosWithRisks.toLocaleString()} out of {merchantVideos.toLocaleString()} videos
                        </p>
                        <p className="text-sm text-gray-400 mt-3">
                          Percentage of live TikTok
                          <br />
                          videos with brand safety risks
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex-1 self-stretch flex justify-start items-center gap-4">
          {/* Card 2: Total videos with risk */}
          <MetricCard
            title="Total videos with risk across all brands"
            value={videosWithRiskCount.toLocaleString()}
            description="Videos with risks that have not been actioned (live status)"
            className="flex-1 self-stretch"
          />

          {/* Card 3: Total risks detected */}
          <MetricCard
            title="Total risks detected across all brands"
            value={totalFlags.toLocaleString()}
            description="All time count of risks detected across all videos"
            className="flex-1 self-stretch"
          />

          {/* Card 4: Total Videos Analysed */}
          <MetricCard
            title="Total videos analysed across all brands"
            value={totalVideos.toLocaleString()}
            description="All time count of videos analysed for safety"
            className="flex-1 self-stretch"
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// RISK COMPARISON TABLE
// ============================================================================

interface RiskComparisonTableProps {
  data: PortfolioAssuranceOverview;
  merchants: Merchant[];
}

const RiskComparisonTable: React.FC<RiskComparisonTableProps> = ({ data, merchants }) => {
  const rows = Object.entries(data).map(([merchantId, metrics]) => {
    const merchant = merchants.find((m) => m.id === merchantId);
    const videosWithRisks = Math.round(metrics.totalVideos * metrics.videosWithFlagsPercentageAfterAddedAndDismissed);
    const percentageWithRisks = Math.round(metrics.videosWithFlagsPercentageAfterAddedAndDismissed * 100);
    const topRisk = metrics.topFlagRiskTypes?.[0];

    return {
      merchantId,
      merchantName: merchant?.name ?? merchantId,
      merchantLogo: merchant?.logoUrl ?? null,
      percentageWithRisks,
      // TODO: percentageChange requires historical data - not currently available from API
      percentageChange: null as number | null,
      videosWithRisks,
      videosAnalysed: metrics.totalVideos,
      mostCommonRisk: topRisk ? { name: topRisk.riskTypeShort, count: topRisk.count } : null,
    };
  });

  return (
    <div className="w-full bg-primary rounded-lg px-8 py-6">
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-tertiary" />
          <h4 className="text-base font-medium text-secondary">Risk Comparison</h4>
        </div>

        <TableCard.Root className="bg-secondary_alt">
          <Table aria-label="Risk Comparison" selectionMode="none">
            <Table.Header>
              <Table.Head isRowHeader className="font-medium text-xs w-1/3">
                Brand
              </Table.Head>
              <Table.Head className="text-xs font-medium">% Videos with Risks</Table.Head>
              <Table.Head className="text-xs font-medium"># Videos with Risks</Table.Head>
              <Table.Head className="text-xs font-medium">Videos Analysed</Table.Head>
              <Table.Head className="text-xs font-medium">Most Common Risk</Table.Head>
            </Table.Header>
            <Table.Body items={rows}>
              {(row) => (
                <Table.Row id={row.merchantId} className="bg-primary">
                  <Table.Cell className="text-sm font-medium">
                    <Link
                      to="/merchant/$merchantId/safety-overview"
                      params={{ merchantId: row.merchantId }}
                      target="_blank"
                    >
                      <div className="flex items-center gap-3 text-brand-secondary hover:underline">
                        {row.merchantLogo ? (
                          <img src={row.merchantLogo} alt={row.merchantName} className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">
                            {row.merchantName.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        {row.merchantName}
                      </div>
                    </Link>
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    <div className="flex items-center gap-2">
                      <span>{row.percentageWithRisks}%</span>
                      {/* TODO: percentageChange requires historical data */}
                      {row.percentageChange !== null && (
                        <span
                          className={cx(
                            "flex items-center gap-0.5 text-xs",
                            row.percentageChange > 0 ? "text-error-600" : "text-success-600",
                          )}
                        >
                          {row.percentageChange > 0 ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )}
                          {Math.abs(row.percentageChange)}%
                        </span>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-sm">{row.videosWithRisks.toLocaleString()}</Table.Cell>
                  <Table.Cell className="text-sm">{row.videosAnalysed.toLocaleString()}</Table.Cell>
                  <Table.Cell className="text-sm">
                    {row.mostCommonRisk ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-xs font-medium">
                        {row.mostCommonRisk.name} - {row.mostCommonRisk.count}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </TableCard.Root>
      </div>
    </div>
  );
};

// ============================================================================
// AVERAGE RESOLUTION TIME TABLE
// ============================================================================

interface AverageResolutionTimeTableProps {
  data: PortfolioAssuranceOverview;
  merchants: Merchant[];
}

type ResolutionTransition =
  | "needs_reviewing_to_needs_action"
  | "needs_action_to_risk_actioned"
  | "needs_reviewing_to_risk_actioned";

const TRANSITION_LABELS: Record<ResolutionTransition, string> = {
  needs_reviewing_to_needs_action: "Needs reviewing → Needs Action",
  needs_action_to_risk_actioned: "Needs action → Actioned",
  needs_reviewing_to_risk_actioned: "Needs Reviewing → Actioned",
};

const AverageResolutionTimeTable: React.FC<AverageResolutionTimeTableProps> = ({ data, merchants }) => {
  const merchantEntries = Object.entries(data);

  // Helper to find a specific transition for a merchant
  const getTransitionDays = (
    metrics: PortfolioAssuranceMetrics,
    fromStatus: string,
    toStatus: string,
  ): number | null => {
    const transition = metrics.averageResolutionTime?.find(
      (t) => t.fromStatus === fromStatus && t.toStatus === toStatus,
    );
    return transition?.averageDays ?? null;
  };

  const rows = merchantEntries.map(([merchantId, metrics]) => {
    const merchant = merchants.find((m) => m.id === merchantId);
    return {
      merchantId,
      merchantName: merchant?.name ?? merchantId,
      merchantLogo: merchant?.logoUrl ?? null,
      needsReviewingToNeedsAction: getTransitionDays(metrics, "needs_reviewing", "needs_action"),
      needsActionToActioned: getTransitionDays(metrics, "needs_action", "risk_actioned"),
      needsReviewingToActioned: getTransitionDays(metrics, "needs_reviewing", "risk_actioned"),
    };
  });

  return (
    <div className="w-full bg-primary rounded-lg px-8 py-6">
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-tertiary" />
          <h4 className="text-base font-medium text-secondary">Average Resolution Time</h4>
        </div>

        <TableCard.Root className="bg-secondary_alt">
          <Table aria-label="Average Resolution Time" selectionMode="none">
            <Table.Header>
              <Table.Head isRowHeader className="font-medium text-xs w-1/3">
                Brand
              </Table.Head>
              <Table.Head className="text-xs font-medium">
                {TRANSITION_LABELS.needs_reviewing_to_needs_action}
              </Table.Head>
              <Table.Head className="text-xs font-medium">{TRANSITION_LABELS.needs_action_to_risk_actioned}</Table.Head>
              <Table.Head className="text-xs font-medium">
                {TRANSITION_LABELS.needs_reviewing_to_risk_actioned}
              </Table.Head>
            </Table.Header>
            <Table.Body items={rows}>
              {(row) => (
                <Table.Row id={row.merchantId} className="bg-primary">
                  <Table.Cell className="text-sm font-medium">
                    <Link
                      to="/merchant/$merchantId/safety-overview"
                      params={{ merchantId: row.merchantId }}
                      target="_blank"
                    >
                      <div className="flex items-center gap-3 text-brand-secondary hover:underline">
                        {row.merchantLogo ? (
                          <img src={row.merchantLogo} alt={row.merchantName} className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">
                            {row.merchantName.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        {row.merchantName}
                      </div>
                    </Link>
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    {row.needsReviewingToNeedsAction !== null ? `${row.needsReviewingToNeedsAction} days` : "-"}
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    {row.needsActionToActioned !== null ? `${row.needsActionToActioned} days` : "-"}
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    {row.needsReviewingToActioned !== null ? `${row.needsReviewingToActioned} days` : "-"}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </TableCard.Root>
      </div>
    </div>
  );
};

// ============================================================================
// VIDEOS POSTED CHART
// ============================================================================

interface VideosPostedChartProps {
  data: PortfolioAssuranceOverview;
}

const MONTH_LABELS: Record<string, string> = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "Apr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
};

const VideosPostedChart: React.FC<VideosPostedChartProps> = ({ data }) => {
  // Aggregate monthly stats across all merchants
  const merchantEntries = Object.entries(data);
  const monthlyMap = new Map<string, { videosPosted: number; videosWithRisks: number }>();

  for (const [, metrics] of merchantEntries) {
    if (metrics.monthlyStats) {
      for (const stat of metrics.monthlyStats) {
        const existing = monthlyMap.get(stat.month) ?? { videosPosted: 0, videosWithRisks: 0 };
        monthlyMap.set(stat.month, {
          videosPosted: existing.videosPosted + stat.videosPosted,
          videosWithRisks: existing.videosWithRisks + stat.videosWithRisks,
        });
      }
    }
  }

  // Convert to chart data and sort by month
  const chartData = Array.from(monthlyMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, stats]) => {
      const monthPart = month.split("-")[1];
      return {
        month,
        label: MONTH_LABELS[monthPart] ?? month,
        videosPosted: stats.videosPosted,
        percentageWithRisks:
          stats.videosPosted > 0 ? Math.round((stats.videosWithRisks / stats.videosPosted) * 100) : 0,
      };
    });

  if (chartData.length === 0) {
    return null;
  }

  // Calculate date range for header
  const firstMonth = chartData[0];
  const lastMonth = chartData[chartData.length - 1];
  const formatDateRange = (month: string) => {
    const [year, m] = month.split("-");
    const date = new Date(Number(year), Number(m) - 1);
    return new Intl.DateTimeFormat("en-US", { month: "short", year: "2-digit" }).format(date);
  };
  const dateRange = `All Time: ${formatDateRange(firstMonth.month)} - ${formatDateRange(lastMonth.month)}`;

  return (
    <div className="w-full bg-primary rounded-lg px-8 py-6">
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <VideoRecorder className="w-5 h-5 text-tertiary" />
            <h4 className="text-base font-medium text-secondary">#Videos posted vs % with risks</h4>
          </div>
          <span className="text-xs text-tertiary">{dateRange}</span>
        </div>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6B7280" }} />
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#6B7280" }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#6B7280" }}
                tickFormatter={(value) => `${value}%`}
                domain={[0, 100]}
              />
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4 min-w-48">
                        <p className="text-base font-semibold text-gray-900">
                          {item.videosPosted.toLocaleString()} videos posted
                        </p>
                        <p className="text-sm text-gray-500">{item.percentageWithRisks}% of those had risks</p>
                        <p className="text-sm text-gray-400 mt-3">This is across all portfolios</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar yAxisId="left" dataKey="videosPosted" fill="#4B5563" radius={[4, 4, 0, 0]} maxBarSize={112} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="percentageWithRisks"
                stroke="#A78BFA"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => {
  return (
    <div
      className={cx(
        "w-full h-full",
        "inline-flex flex-col justify-start items-center gap-12",
        "bg-gradient-to-b from-[#FFE1BD]/40 via-[#FFF9C5]/40 to-[#FFFFFF]/15",
      )}
    >
      <div className={cx(LAYOUT_CONSTRAINTS.CONTAINER_WIDTH, "flex flex-col gap-8 px-16 pt-16 pb-8")}>
        <DashboardHeader />

        {/* Top Metrics - pie chart + 3 metric cards */}
        <div className="self-stretch p-8 bg-primary rounded-lg inline-flex flex-col justify-start items-start gap-12">
          <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
          <div className="self-stretch bg-primary rounded-lg inline-flex justify-start items-start gap-20">
            {/* Pie chart skeleton */}
            <div className="w-80 h-60 flex items-center justify-center">
              <div className="w-[230px] h-[230px] rounded-full animate-pulse bg-gray-200" />
            </div>
            {/* 3 metric cards */}
            <div className="flex-1 self-stretch flex justify-start items-center gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex-1 self-stretch bg-gray-50 rounded-xl p-6 flex flex-col gap-4">
                  <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                  <div className="h-8 w-24 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Risk Comparison Table */}
        <div className="w-full bg-primary rounded-lg px-8 py-6">
          <div className="flex flex-col gap-6">
            <div className="h-5 w-36 animate-pulse rounded bg-gray-200" />
            <div className="h-64 animate-pulse rounded-lg bg-gray-100" />
          </div>
        </div>

        {/* Average Resolution Time Table */}
        <div className="w-full bg-primary rounded-lg px-8 py-6">
          <div className="flex flex-col gap-6">
            <div className="h-5 w-48 animate-pulse rounded bg-gray-200" />
            <div className="h-48 animate-pulse rounded-lg bg-gray-100" />
          </div>
        </div>

        {/* Videos Posted Chart */}
        <div className="w-full bg-primary rounded-lg px-8 py-6">
          <div className="flex flex-col gap-6">
            <div className="h-5 w-56 animate-pulse rounded bg-gray-200" />
            <div className="h-64 animate-pulse rounded-lg bg-gray-100" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Renders the safety overview for a given set of merchants. Shared between the global
// portfolio ("Your Portfolio") and a Workspace Admin's workspace safety overview (UNB-1953) —
// both surface identical content, just scoped to a different merchant list.
export const PortfolioSafetyContent: React.FC<{ merchants: Merchant[]; workspaceId?: string }> = ({
  merchants,
  workspaceId,
}) => {
  const assuranceMerchants = merchants.filter((m) => m.features.includes("assurance"));
  const { data, isLoading } = usePortfolioAssuranceOverview(workspaceId);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!data) {
    return (
      <div
        className={cx(
          "w-full h-full",
          "inline-flex flex-col justify-start items-center gap-12",
          "bg-gradient-to-b from-[#FFE1BD]/40 via-[#FFF9C5]/40 to-[#FFFFFF]/15",
        )}
      >
        <div className={cx(LAYOUT_CONSTRAINTS.CONTAINER_WIDTH, "flex flex-col gap-12 px-16 pt-16 pb-8")}>
          <DashboardHeader />
          <div className="w-full text-center py-12 text-gray-500">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(
        "w-full h-full",
        "inline-flex flex-col justify-start items-center gap-12",
        "bg-gradient-to-b from-[#FFE1BD]/40 via-[#FFF9C5]/40 to-[#FFFFFF]/15",
      )}
    >
      <div className={cx(LAYOUT_CONSTRAINTS.CONTAINER_WIDTH, "flex flex-col gap-8 px-16 pt-16 pb-16")}>
        <DashboardHeader merchants={assuranceMerchants} />
        <TopMetrics data={data} merchants={assuranceMerchants} />
        <RiskComparisonTable data={data} merchants={assuranceMerchants} />
        <AverageResolutionTimeTable data={data} merchants={assuranceMerchants} />
        <VideosPostedChart data={data} />
      </div>
    </div>
  );
};

export const PortfolioSafetyOverview: React.FC = () => {
  const { merchants, enabled } = usePortfolio();
  if (enabled === false) {
    return <Navigate to="/" />;
  }
  return <PortfolioSafetyContent merchants={merchants} />;
};
